<template>
  <v-dialog v-model="visible" max-width="750px" persistent>
    <v-card>
      <v-card-title class="py-2 d-flex flex-row align-center">
        <span class="text-h6">
          {{ $t("managedInstructions.createInstructionDialog.title") }}
        </span>

        <v-tabs v-model="langIndex" class="tabs">
          <v-tab>Deutsch</v-tab>
          <v-tab>English</v-tab>
        </v-tabs>
      </v-card-title>

      <v-card-text class="pt-2">
        <v-form ref="form" v-model="formValid">
          <v-select
            v-if="isOwner"
            dense
            class="mb-4"
            hide-details="auto"
            v-model="form.type"
            :items="typeOptions"
            :label="$t('managedInstructions.createInstructionDialog.type')"
            :rules="[rules.required]"
            item-text="label"
            item-value="value"
          />

          <v-text-field
            dense
            class="mb-4"
            hide-details="auto"
            v-model="nameModel"
            :label="$t('managedInstructions.createInstructionDialog.name')"
            :rules="[rules.requiredLang(activeLang)]"
          />

          <v-text-field
            dense
            class="mb-4"
            hide-details="auto"
            v-model="descriptionModel"
            :label="
              $t('managedInstructions.createInstructionDialog.description')
            "
            :rules="[rules.requiredLang(activeLang)]"
          />

          <v-file-input
            dense
            class="mb-4"
            hide-details="auto"
            v-model="form.previewImageFile"
            :label="$t('managedInstructions.createInstructionDialog.image')"
            accept="image/*"
            prepend-inner-icon="mdi-paperclip"
            prepend-icon=""
          />

          <div class="d-flex justify-space-between align-center mb-2">
            <div class="d-flex">
              <v-btn small outlined class="px-3 mr-2" @click="addFileStep">
                {{ $t("managedInstructions.createInstructionDialog.addFile") }}
              </v-btn>
              <v-btn small outlined class="px-3 mr-2" @click="addVideoStep">
                {{ $t("managedInstructions.createInstructionDialog.addVideo") }}
              </v-btn>
              <v-btn small outlined class="px-3 mr-2" @click="addSingleChoiceStep">
                {{
                  $t("managedInstructions.createInstructionDialog.addQuestion")
                }}
              </v-btn>
              <v-btn small outlined class="px-3" @click="addLinkStep">
                {{ $t("managedInstructions.createInstructionDialog.addLink") }}
              </v-btn>
            </div>
          </div>

          <v-expansion-panels v-if="currentSteps.length" multiple accordion>
            <draggable
              :list="currentSteps"
              tag="div"
              class="steps-draggable"
              ghost-class="step-ghost"
            >
            <v-expansion-panel v-for="(step, i) in currentSteps" :key="step.id">
              <v-expansion-panel-header
                class="py-1 step-draggable-header"
                :class="{ 'step-highlight': highlightedStepId === step.id }"
              >
                <div class="d-flex justify-space-between align-center w-100">
                  <span class="text-body-2">{{ stepTitle(step, i) }}</span>

                  <div class="d-flex align-center mr-2">
                    <v-btn icon small @click.stop="removeStep(i)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-2 pb-2">
                <template v-if="step.uiType === 'file'">
                  <v-file-input
                    dense
                    class="mb-2"
                    hide-details="auto"
                    v-model="step.localFile"
                    :label="
                      $t('managedInstructions.createInstructionDialog.file')
                    "
                    accept=".pdf,.doc,.docx,.ppt,.pptx"
                    :rules="[rules.requiredStepFile(activeLang, step)]"
                  />
                </template>

                <template v-else-if="step.uiType === 'video'">
                  <v-file-input
                    dense
                    class="mb-2"
                    hide-details="auto"
                    v-model="step.localFile"
                    :label="
                      $t('managedInstructions.createInstructionDialog.video')
                    "
                    accept="video/*,.mp4,.mkv,.mov"
                    :rules="[rules.requiredStepFile(activeLang, step)]"
                  />
                </template>

                <template v-else-if="step.uiType === 'singleChoiceQuestion'">
                  <v-text-field
                    dense
                    class="mb-2"
                    hide-details="auto"
                    v-model="step.question"
                    :label="
                      $t('managedInstructions.createInstructionDialog.question')
                    "
                    :rules="[rules.requiredLang(activeLang)]"
                  />

                  <v-radio-group
                    v-model="step.correct"
                    :mandatory="false"
                    class="mb-1"
                    :rules="[rules.correctAnswerLang(activeLang, step)]"
                  >
                    <div
                      v-for="(answer, idx) in step.answers"
                      :key="idx"
                      class="d-flex align-center mb-2"
                    >
                      <v-radio
                        class="mr-2 mb-n2"
                        :value="idx"
                        :disabled="!answer || !answer.toString().trim()"
                      />
                      <v-text-field
                        dense
                        hide-details="auto"
                        v-model="step.answers[idx]"
                        :label="answerLabel(idx)"
                        :rules="idx < 2 ? [rules.requiredLang(activeLang)] : []"
                        class="flex-grow-1"
                      />
                    </div>
                  </v-radio-group>
                </template>

                <template v-else-if="step.uiType === 'link'">
                  <v-text-field
                    dense
                    class="mb-4"
                    hide-details="auto"
                    v-model="step.title"
                    :label="
                      $t('managedInstructions.createInstructionDialog.linkTitle')
                    "
                    :rules="[rules.requiredLang(activeLang)]"
                  />

                  <v-textarea
                    dense
                    rows="1"
                    auto-grow
                    class="mb-4"
                    hide-details="auto"
                    v-model="step.text"
                    :label="
                      $t('managedInstructions.createInstructionDialog.linkText')
                    "
                  />

                  <v-text-field
                    dense
                    class="mb-4"
                    hide-details="auto"
                    v-model="step.url"
                    :label="
                      $t('managedInstructions.createInstructionDialog.linkUrl')
                    "
                    placeholder="https://"
                    :rules="[rules.urlLang(activeLang)]"
                  />
                </template>
              </v-expansion-panel-content>
            </v-expansion-panel>
            </draggable>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-card-actions class="py-2">
        <v-spacer />
        <v-btn text small @click="closeDialog" :disabled="loading">
          {{ $t("general.cancel") }}
        </v-btn>
        <v-btn
          small
          color="primary"
          :loading="loading"
          :disabled="!formValid"
          @click="onSave"
        >
          {{ $t("general.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import draggable from "vuedraggable";
let stepIdCounter = 1;

export default {
  name: "ManagedInstructionCreateDialog",
  components: { draggable },

  data() {
    return {
      LANGS: ["GER", "ENG"],
      langIndex: 0,

      visible: false,
      formValid: false,
      loading: false,
      isOwner: false,

      form: this.getEmptyForm(),

      highlightedStepId: null,

      typeOptions: [
        {
          value: "global",
          label: this.$t("managedInstructions.createInstructionDialog.global"),
        },
        {
          value: "local",
          label: this.$t("managedInstructions.createInstructionDialog.local"),
        },
      ],

      rules: {
        required: (v) =>
          !!(v && v.toString().trim()) || this.$t("general.fieldRequired"),

        requiredLang: (lang) => (v) => {
          const val = v ? v.toString().trim() : "";
          if (lang === "GER") return !!val || this.$t("general.fieldRequired");
          if (!this.isEnglishEnabled) return true;
          return !!val || this.$t("general.fieldRequired");
        },

        requiredStepFile: (lang) => (v) => {
          if (lang === "ENG" && !this.isEnglishEnabled) return true;

          const file = this.normalizeFile(v);
          if (lang === "GER") return !!file || this.$t("general.fieldRequired");

          return !!file || this.$t("general.fieldRequired");
        },

        correctAnswerLang: (lang, step) => (value) => {
          if (lang === "ENG" && !this.isEnglishEnabled) return true;

          const answers = (step.answers || []).map((a) =>
            a ? a.toString().trim() : "",
          );
          const filled = answers.filter((a) => a !== "");

          if (filled.length < 2) {
            return this.$t(
              "managedInstructions.createInstructionDialog.twoAnswersRequired",
            );
          }
          if (value === null || value === undefined) {
            return this.$t(
              "managedInstructions.createInstructionDialog.chooseCorrectAnswer",
            );
          }
          if (!answers[value]) {
            return this.$t(
              "managedInstructions.createInstructionDialog.correctAnswerMustHaveText",
            );
          }
          return true;
        },

        urlLang: (lang) => (v) => {
          if (lang === "ENG" && !this.isEnglishEnabled) return true;

          const val = v ? v.toString().trim() : "";
          if (!val) return this.$t("general.fieldRequired");
          if (!/^https?:\/\/.+/i.test(val)) {
            return this.$t(
              "managedInstructions.createInstructionDialog.invalidUrl",
            );
          }
          return true;
        },
      },
    };
  },

  created() {
    this.loadUserRole();
  },

  computed: {
    activeLang() {
      return this.LANGS[this.langIndex] || "GER";
    },

    currentSteps() {
      return this.form.stepsByLang[this.activeLang] || [];
    },

    isEnglishEnabled() {
      const t = this.form.translations.ENG || { name: "", description: "" };
      const hasText = !!((t.name || "").trim() || (t.description || "").trim());
      const hasSteps = (this.form.stepsByLang.ENG?.length || 0) > 0;
      return hasText || hasSteps;
    },

    nameModel: {
      get() {
        return this.form.translations[this.activeLang]?.name || "";
      },
      set(v) {
        if (!this.form.translations[this.activeLang]) {
          this.$set(this.form.translations, this.activeLang, {
            name: "",
            description: "",
          });
        }
        this.$set(this.form.translations[this.activeLang], "name", v);
      },
    },

    descriptionModel: {
      get() {
        return this.form.translations[this.activeLang]?.description || "";
      },
      set(v) {
        if (!this.form.translations[this.activeLang]) {
          this.$set(this.form.translations, this.activeLang, {
            name: "",
            description: "",
          });
        }
        this.$set(this.form.translations[this.activeLang], "description", v);
      },
    },
  },

  methods: {
    highlightMovedStep(stepId) {
      this.highlightedStepId = stepId;

      clearTimeout(this._highlightTimeout);
      this._highlightTimeout = setTimeout(() => {
        this.highlightedStepId = null;
      }, 550);
    },

    async loadUserRole() {
      try {
        const user = await this.$store.dispatch("getUserInfo");
        this.isOwner = user && isMaster(user.email);
      } catch (e) {
        this.isOwner = false;
        console.error("Failed to load user info", e);
      }
    },

    getEmptyForm() {
      return {
        type: "local",
        translations: {
          GER: { name: "", description: "" },
          ENG: { name: "", description: "" },
        },
        previewImageFile: null,
        stepsByLang: {
          GER: [],
          ENG: [],
        },
      };
    },

    openDialog() {
      this.langIndex = 0;
      this.form = this.getEmptyForm();
      this.visible = true;
      this.highlightedStepId = null;

      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },

    closeDialog() {
      this.visible = false;
      this.highlightedStepId = null;
    },

    normalizeFile(input) {
      if (!input) return null;
      if (Array.isArray(input)) return input[0] || null;
      return input;
    },

    stepTitle(step, idx) {
      const n = `#${idx + 1}`;
      if (step.uiType === "file") {
        return this.$t("managedInstructions.createInstructionDialog.stepFile", {
          number: n,
        });
      }
      if (step.uiType === "video") {
        return this.$t(
          "managedInstructions.createInstructionDialog.stepVideo",
          { number: n },
        );
      }
      if (step.uiType === "singleChoiceQuestion") {
        return this.$t(
          "managedInstructions.createInstructionDialog.stepQuestion",
          { number: n },
        );
      }
      if (step.uiType === "link") {
        return this.$t("managedInstructions.createInstructionDialog.stepLink", {
          number: n,
        });
      }
      return `Step ${idx + 1}`;
    },

    answerLabel(idx) {
      const base = this.$t(
        "managedInstructions.createInstructionDialog.answer",
      );
      return `${base} ${idx + 1}`;
    },

    addFileStep() {
      this.form.stepsByLang[this.activeLang].push({
        id: stepIdCounter++,
        uiType: "file",
        localFile: null,
        uploadedAt: new Date(),
      });
    },

    addVideoStep() {
      this.form.stepsByLang[this.activeLang].push({
        id: stepIdCounter++,
        uiType: "video",
        localFile: null,
        uploadedAt: new Date(),
      });
    },

    addSingleChoiceStep() {
      this.form.stepsByLang[this.activeLang].push({
        id: stepIdCounter++,
        uiType: "singleChoiceQuestion",
        uploadedAt: new Date(),
        question: "",
        answers: ["", "", "", ""],
        correct: null,
      });
    },

    addLinkStep() {
      this.form.stepsByLang[this.activeLang].push({
        id: stepIdCounter++,
        uiType: "link",
        uploadedAt: new Date(),
        title: "",
        text: "",
        url: "",
      });
    },

    removeStep(i) {
      this.form.stepsByLang[this.activeLang].splice(i, 1);
    },

    buildNameArray() {
      const out = [
        {
          language: "GER",
          name: (this.form.translations.GER.name || "").trim(),
        },
      ];

      if (this.isEnglishEnabled) {
        const en = (this.form.translations.ENG.name || "").trim();
        if (en) out.push({ language: "ENG", name: en });
      }

      return out;
    },

    isVideoExt(ext) {
      return ["mp4", "mkv", "mov", "webm", "m4v"].includes(
        (ext || "").toLowerCase(),
      );
    },

    async presignVideoUpload(filename) {
      const res = await this.$store.dispatch(
        "getManagedPresignVideoUpload",
        filename,
      );

      return res;
    },

    async uploadVideoToMinioPresigned(url, file) {
      const resp = await fetch(url, {
        method: "PUT",
        body: file,
      });

      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`MinIO upload failed (${resp.status}): ${text}`);
      }
    },

    buildDescriptionArray() {
      const out = [
        {
          language: "GER",
          description: (this.form.translations.GER.description || "").trim(),
        },
      ];

      if (this.isEnglishEnabled) {
        const en = (this.form.translations.ENG.description || "").trim();
        if (en) out.push({ language: "ENG", description: en });
      }

      return out;
    },

    fileExt(file) {
      const name = file?.name || "";
      if (!name.includes(".")) return "";
      return name.split(".").pop().toLowerCase();
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          if (typeof result === "string") {
            const base64 = result.includes(",") ? result.split(",")[1] : result;
            resolve(base64);
          } else {
            reject(new Error("Unexpected FileReader result type"));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    resolveBackendType(step) {
      if (step.uiType === "singleChoiceQuestion") return "singleChoiceQuestion";
      if (step.uiType === "link") return "link";

      const file = this.normalizeFile(step.localFile);
      const ext = this.fileExt(file);

      const allowed = [
        "doc",
        "docx",
        "pdf",
        "ppt",
        "pptx",
        "mp4",
        "mkv",
        "mov",
      ];
      if (!allowed.includes(ext)) {
        throw new Error(`Unsupported file type: ${ext || "(none)"}`);
      }
      return ext;
    },

    async buildStepsPayload() {
      const result = [];
      const langs = ["GER", "ENG"];

      for (const lang of langs) {
        if (lang === "ENG" && !this.isEnglishEnabled) continue;

        const drafts = this.form.stepsByLang[lang] || [];
        const steps = [];

        for (const step of drafts) {
          const type = this.resolveBackendType(step);

          if (type === "singleChoiceQuestion") {
            steps.push({
              type: "singleChoiceQuestion",
              data: [
                {
                  uploadedAt: step.uploadedAt || new Date(),
                  question: (step.question || "").trim(),
                  answers: (step.answers || []).map((a) =>
                    (a ?? "").toString().trim(),
                  ),
                  correct: typeof step.correct === "number" ? step.correct : 0,
                },
              ],
            });
            continue;
          }

          if (type === "link") {
            steps.push({
              type: "link",
              data: [
                {
                  uploadedAt: step.uploadedAt || new Date(),
                  title: (step.title || "").trim(),
                  text: (step.text || "").trim(),
                  url: (step.url || "").trim(),
                },
              ],
            });
            continue;
          }

          const file = Array.isArray(step.localFile)
            ? step.localFile[0]
            : step.localFile;
          if (!file) continue;

          const name = file.name || "file";
          const filetype = this.fileExt(file);

          if (step.uiType === "video" || this.isVideoExt(filetype)) {
            const { url, location } = await this.presignVideoUpload(name);
            await this.uploadVideoToMinioPresigned(url, file);

            steps.push({
              type: filetype,
              data: [
                {
                  uploadedAt: step.uploadedAt || new Date(),
                  location,
                },
              ],
            });

            continue;
          }

          const content = await this.fileToBase64(file);

          steps.push({
            type,
            data: [
              {
                name,
                filetype,
                content,
              },
            ],
          });
        }

        result.push({ language: lang, steps });
      }

      return result;
    },

    async onSave() {
      if (!this.$refs.form) return;

      const valid = this.$refs.form.validate();
      if (!valid) return;

      this.loading = true;
      try {
        let previewImagePayload = null;

        if (this.form.previewImageFile) {
          const file = Array.isArray(this.form.previewImageFile)
            ? this.form.previewImageFile[0]
            : this.form.previewImageFile;

          if (file) {
            const name = file.name || "preview";
            const parts = name.split(".");
            const filetype =
              parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";

            const content = await this.fileToBase64(file);

            previewImagePayload = {
              name,
              filetype,
              content,
            };
          }
        }

        const steps = await this.buildStepsPayload();

        const payload = {
          type: this.form.type,
          name: this.buildNameArray(),
          description: this.buildDescriptionArray(),
          previewImage: previewImagePayload,
          steps,
        };

        await this.$store.dispatch("createManagedInstruction", payload);

        this.$emit("saved");
        this.closeDialog();
      } catch (e) {
        console.error("Failed to create instruction", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.tabs {
  width: fit-content;
  margin-left: 20px;
}

.step-highlight {
  transition: background-color 0.1s ease;
  background-color: #bebebe37;
}
.steps-draggable {
  width: 100%;
}
.steps-draggable > .v-expansion-panel {
  width: 100%;
}
.step-draggable-header {
  cursor: grab;
}
.step-draggable-header:active {
  cursor: grabbing;
}
.step-ghost {
  opacity: 0.5;
  background-color: #bebebe37;
}
</style>
